import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './VerificationListTable.module.scss';
import { DataTableWithSidebar, DataTableColumn, OrderData } from 'common/components/dataTable';
import { CarVerificationListItemDto, RemoveItemViewModel } from 'api/carVerification/models/CarVerificationDto';
import { CarVerificationSearchCriteria } from 'api/carVerification/models/CarVerificationSearchCriteria';
import { VerificationType } from 'api/carVerification/enums/VerificationType';
import { InterventionTypeEnum } from 'api/carVerification/enums/InterventionTypeEnum';
import { getInterventionTypeStatusColor, isTripLikeVerification } from 'common/utils/VerificationsUtils';
import { DATE_FORMAT_DEFAUT, DefaultAssetImage, LOGGER_LOG_TYPE, SEGMENT_FEATURES } from 'Config';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import IconWithText from 'common/components/iconWithText/IconWithText';
import Badge from 'common/components/badge/Badge';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import RemoveVerificationModal from 'screens/vehicle/components/carVerificationTab/components/removeVerificationModal/RemoveVerificationModal';
import VehicleReplacementModal from 'screens/vehicle/components/vehicleReplacementModal/VehicleReplacementModal';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { FaEllipsisH } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import GaugeIcon from 'assets/svg/gauge.svg';
import DriveIcon from 'assets/svg/drive.svg';
import VerificationsIcon from 'assets/svg/i_verificaçoes_carro.svg';
import CarburantIcon from 'assets/svg/desktop_frais_carburant.svg';
import PlayIcon from 'assets/svg/play2.svg';
import FlagBWIcon from 'assets/svg/flag_bw.svg';
import HourGlassIcon from 'assets/svg/hour-glass.svg';
import CalendarvIcon from 'assets/svg/calendar_v_routine.svg';
import ClockIcon from 'assets/svg/desktop_i_clock.svg';
import colors from 'styles/export/colors.module.scss';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import CarVerificationService from 'api/carVerification/CarVerificationService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import { useAdditionalDataListingColumns } from 'common/hooks/useAdditionalDataListingColumns';

type VerificationTableRow = CarVerificationListItemDto & {
    vehicleRegistration: string;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleImage: string;
    typeName: string;
    driverDisplay: string;
    startDateDisplay: Date | undefined;
    endDateDisplay: Date | undefined;
    kilometersDisplay: number | string;
    kmStartDisplay: number | string;
    kmEndDisplay: number | string;
    kmTripDisplay: number | string;
    autonomyDisplay: number | string;
    hoursStartDisplay: number | string;
    hoursEndDisplay: number | string;
    hoursTotalDisplay: number | string;
    annotationsDisplay: string;
    vehicleSegmentDisplay: string;
    vehicleLocalDisplay: string;
    vehicleSegmentFeaturesArr: string[];
};

const hasFeature = (row: { vehicleSegmentFeaturesArr?: string[] }, feature: string): boolean =>
    (row.vehicleSegmentFeaturesArr ?? []).includes(feature);

export type Props = {
    criteria?: CarVerificationSearchCriteria;
    vehicleId?: string;
    editable: boolean;
    items: CarVerificationListItemDto[];
    totalItems: number;
    isLoadingMore: boolean;
    hasMore: boolean;
    onTableFilter: (field: string, isFilterAsc: boolean) => void;
    onLoadMore: () => void;
    onClickItem: (item: CarVerificationListItemDto, event: any) => void;
    onDeleteFromList?: () => void;
    reRenderSearch: number;
    scrollToRowId?: string | null;
    hasMorePrevious?: boolean;
    isLoadingPrevious?: boolean;
    onLoadPrevious?: () => void;
    vehicle?: VehicleDto;
    onVerificationsUpdated?: () => void | Promise<void>;
    onFetchAllForExport?: () => Promise<CarVerificationListItemDto[]>;
    exportRef?: React.MutableRefObject<{ exportExcel: () => Promise<void> } | null>;
};

const VerificationListTable = ({
    criteria,
    vehicleId,
    editable,
    items,
    totalItems,
    isLoadingMore,
    hasMore,
    onTableFilter,
    onLoadMore,
    onClickItem,
    onDeleteFromList,
    reRenderSearch,
    scrollToRowId,
    hasMorePrevious,
    isLoadingPrevious,
    onLoadPrevious,
    vehicle,
    onVerificationsUpdated,
    onFetchAllForExport,
    exportRef,
}: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const { tabId } = useParams<{ tabId: string }>();

    const imageCacheKey = useRef(new Date().getTime());
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVerificationsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VERIFICATIONS_WRITE']);
    const { columns: customFieldColumns } = useAdditionalDataListingColumns<VerificationTableRow>('VERIFICATION');

    const [itemToRemove, setItemToRemove] = useState<CarVerificationListItemDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [showRemoveWithReplacementModal, setShowRemoveWithReplacementModal] = useState(false);
    const [verificationSelected, setVerificationSelected] = useState<CarVerificationListItemDto | null>(null);
    const [dialogReplacementVehicleIsOpen, setDialogReplacementVehicleIsOpen] = useState(false);
    const [lastVerificationImmobilized, setLastVerificationImmobilized] = useState<boolean | undefined>(undefined);

    const showRemoveItemDialog = async (item: CarVerificationListItemDto) => {
        setItemToRemove(item);

        try {
            Loading.show();
            const lastVerification = await CarVerificationService.getLastActiveVerificationByVehicleId(vehicleId ?? item.vehicleId ?? '', item.id ?? '');
            setLastVerificationImmobilized(lastVerification?.immobilized);

            const immobilizedStateChanges = Boolean(item?.immobilized) !== Boolean(lastVerification?.immobilized) && Boolean(lastVerification ? Boolean(vehicle?.immobilized) != lastVerification?.immobilized : true);
            const hasReplacementFromThisVerification = Boolean(vehicle?.replacedVehicle) &&
                vehicle?.replacementVerificationId === item.id;

            if (immobilizedStateChanges || hasReplacementFromThisVerification) {
                setShowRemoveWithReplacementModal(true);
            } else {
                setShowRemoveModal(true);
            }

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get last active verification`, error);
            Loading.hide();
            setShowRemoveModal(true);
        }
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
        setShowRemoveWithReplacementModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            setShowRemoveModal(false);
            setShowRemoveWithReplacementModal(false);
            Loading.show();

            await CarVerificationService.remove(itemToRemove as RemoveItemViewModel);
            onCancelRemove();
            onVerificationsUpdated && await onVerificationsUpdated();
            if (onDeleteFromList) onDeleteFromList();

            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete verification`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const onRemoveAndFinishReplacement = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            setShowRemoveWithReplacementModal(false);
            Loading.show();

            await CarVerificationService.remove(itemToRemove as RemoveItemViewModel);
            onCancelRemove();
            onVerificationsUpdated && await onVerificationsUpdated();
            if (onDeleteFromList) onDeleteFromList();

            setDialogReplacementVehicleIsOpen(true);

            addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete verification`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const renderTableActionCell = (row: VerificationTableRow) => {
        const isVehicle = !!vehicleId;
        const urlDetails = isVehicle ? `/vehicles/details/${vehicleId}/${tabId}/details/${row.id}` : `/verifications/details/${row.id}`;
        const urlEdit = isVehicle ? `/vehicles/details/${vehicleId}/${tabId}/edit/${row.id}` : `/verifications/edit/${row.id}`;

        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasVerificationsWritePolicy && editable && !row.hasAnnotationDone && <DropdownItem url={urlEdit}>
                            {t('common.edit')}
                        </DropdownItem>}
                        {hasVerificationsWritePolicy && editable && !row.hasAnnotationDone && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                        {hasVerificationsWritePolicy && editable && !row.active && !vehicle?.replacedVehicle && vehicle?.immobilized && <DropdownItem onClick={() => { setVerificationSelected(row); setDialogReplacementVehicleIsOpen(true) }}>
                            {t('replacement_vehicles.replace')}
                        </DropdownItem>}
                        {hasVerificationsWritePolicy && editable && !row.active && vehicle?.replacedVehicle && vehicle?.replacementVerificationId === row.id && <DropdownItem onClick={() => { setVerificationSelected(row); setDialogReplacementVehicleIsOpen(true) }}>
                            {t('replacement_vehicles.finish_replacement')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const renderStartDateCell = (row: VerificationTableRow) => {
        const dateBegin = row.verificationDate ? moment(row.verificationDate).toDate() : null;
        if (dateBegin && row.verificationHour && row.verificationHour.length > 0) {
            const parts = row.verificationHour.split(':');
            dateBegin.setHours(Number(parts[0] ?? 0));
            dateBegin.setMinutes(Number(parts[1] ?? 0));
        }

        if (!dateBegin) return <>-</>;

        return (
            <div data-tip={isTripLikeVerification(row.type) ? t('car_verification.list.start_travel_date_tooltip') : t('car_verification.list.verification_date_tooltip')} data-for={`start_${row.id}`}>
                <IconWithText
                    icon={isTripLikeVerification(row.type) ? PlayIcon : CalendarvIcon}
                    valueDate={dateBegin}
                    valueHour={row.verificationHour}
                    isDate={true}
                />
                <ReactTooltip id={`start_${row.id}`} />
            </div>
        );
    };

    const renderEndDateCell = (row: VerificationTableRow) => {
        if (!isTripLikeVerification(row.type)) return <>-</>;

        const dateBegin = row.verificationDate ? moment(row.verificationDate).toDate() : null;
        const dateEnd = row.verificationFinishDate ? moment(row.verificationFinishDate).toDate() : null;
        const isTravel = isTripLikeVerification(row.type);
        const tripInProgress = isTravel && dateBegin && !dateEnd;

        if (dateEnd && row.verificationFinishHour) {
            const parts = row.verificationFinishHour.split(':');
            dateEnd.setHours(Number(parts[0] ?? 0));
            dateEnd.setMinutes(Number(parts[1] ?? 0));
        }

        if (tripInProgress) {
            return (
                <IconWithText
                    icon={HourGlassIcon}
                    value={t('car_verification.list.trip_in_progress')}
                    color={colors.defaultGreen}
                    className={styles.labelSmall}
                />
            );
        }

        if (dateBegin && dateEnd) {
            return (
                <div data-tip={t('car_verification.list.end_travel_date_tooltip')} data-for={`end_${row.id}`}>
                    <IconWithText
                        icon={FlagBWIcon}
                        valueDate={dateEnd}
                        valueHour={row.verificationFinishHour ?? null}
                        isDate={true}
                    />
                    <ReactTooltip id={`end_${row.id}`} />
                </div>
            );
        }

        return <>-</>;
    };

    const renderKilometersCell = (row: VerificationTableRow) => {
        if (isTripLikeVerification(row.type)) return <>-</>;
        if (!hasFeature(row, SEGMENT_FEATURES.KM)) return <>-</>;
        return (
            <div className={styles.cellContainer}>
                <div className={styles.firstRow}>
                    <IconWithText
                        icon={GaugeIcon}
                        value={row.kilometers}
                        suffix={t('car_verification.list.kms')}
                        isNumeric={true}
                    />
                </div>
            </div>
        );
    };

    const renderAnnotationsCell = (row: VerificationTableRow) => {
        const vehicleImmobilizedTooltip = row.predictedDateEndImmobilization
            ? `${t('vehicles.stopped_until')} ${moment(row.predictedDateEndImmobilization).format(DATE_FORMAT_DEFAUT)}`
            : t('vehicles.no_date_end_immobilization');

        const annotations = [
            { counter: row.numberAnnotationsUrgent, color: getInterventionTypeStatusColor(InterventionTypeEnum.INTERVENTION_URGENT).color },
            { counter: row.numberAnnotationsInformative, color: getInterventionTypeStatusColor(InterventionTypeEnum.INFORMATION).color },
            { counter: row.numberAnnotationsIntervention, color: getInterventionTypeStatusColor(InterventionTypeEnum.INTERVENTION).color }
        ];

        return (
            <div>
                <span>
                    <div className={styles.countersContainer}>
                        {annotations.map((x, i) => <Badge className={styles.counterBadge} key={`roundValue_${i}`}
                            backgroundColor={x.color} size={'round'} outlineColor={x.color}>{x.counter}</Badge>)}
                        {row.immobilized && <div className={styles.stopContainer}>
                            <div className={styles.iconStop} data-tip={vehicleImmobilizedTooltip} />
                        </div>}
                    </div>
                </span>
            </div>
        );
    };

    const tableColumns: DataTableColumn<VerificationTableRow>[] = useMemo(() => {
        const cols: DataTableColumn<VerificationTableRow>[] = [
            {
                field: 'typeName',
                name: t('car_verification.list.type') as string,
                orderColumn: 'v.type',
                width: 130,
                exportValue: row => t(`car_verification.annotation_types.${row.type}`.toLowerCase() as any) as string,
                renderCell: row =>
                    <div className={styles.rowFlex}>
                        <IconWithText
                            icon={isTripLikeVerification(row.type) ? DriveIcon : VerificationsIcon}
                            value={t(`car_verification.annotation_types.${row.type}`.toLowerCase() as any)}
                            size={'lg'}
                        />
                    </div>,
            },
            {
                field: 'driverDisplay',
                name: t('car_verification.list.driver') as string,
                orderColumn: 'displayUserName',
                width: 150,
                hideOn: ['sm', 'md', 'lg', 'xl'],
                exportValue: row => isTripLikeVerification(row.type)
                    ? (row.driverName ?? '')
                    : (row.responsibleName ?? ''),
                renderCell: row => isTripLikeVerification(row.type)
                    ? <AvatarWithText src={row.driverPhotoUrl} size="x2" hideAvatar={!row.driverName}><span>{row.driverName || '-'}</span></AvatarWithText>
                    : <AvatarWithText src={row.responsiblePhotoUrl} size="x2" hideAvatar={!row.responsibleName}><span>{row.responsibleName || '-'}</span></AvatarWithText>,
            },
            {
                field: 'startDateDisplay',
                name: t('car_verification.list.start_date') as string,
                orderColumn: 'v.verification_date',
                width: 150,
                hideOn: ['sm', 'md'],
                exportValue: row => row.verificationDate ? moment(row.verificationDate).format(DATE_FORMAT_DEFAUT) + (row.verificationHour ? ' | ' + row.verificationHour : '') : '',
                renderCell: renderStartDateCell,
            },
            {
                field: 'endDateDisplay',
                name: t('car_verification.list.end_date') as string,
                orderColumn: 'v.verification_finish_date',
                width: 150,
                hideOn: ['sm', 'md'],
                exportValue: row => {
                    if (!isTripLikeVerification(row.type)) return '';
                    const dateBegin = row.verificationDate ? moment(row.verificationDate).toDate() : null;
                    const dateEnd = row.verificationFinishDate ? moment(row.verificationFinishDate).toDate() : null;
                    if (dateBegin && !dateEnd) return t('car_verification.list.trip_in_progress') as string;
                    if (dateEnd) return moment(row.verificationFinishDate).format(DATE_FORMAT_DEFAUT) + (row.verificationFinishHour ? ' | ' + row.verificationFinishHour : '');
                    return '';
                },
                renderCell: renderEndDateCell,
            },
            {
                field: 'kilometersDisplay',
                name: t('car_verification.list.kilometers_col') as string,
                orderColumn: 'v.kilometers',
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: `${t('common.unit')}: ${t('car_verification.list.kms')}`,
                renderCell: renderKilometersCell,
            },
            {
                field: 'kmStartDisplay',
                name: t('car_verification.list.km_start') as string,
                orderColumn: 'v.kilometers',
                width: 120,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: `${t('common.unit')}: ${t('car_verification.list.kms')}`,
                exportValue: row => isTripLikeVerification(row.type) && hasFeature(row, SEGMENT_FEATURES.KM) && row.kilometers != null
                    ? String(row.kilometers)
                    : '',
                renderCell: row => isTripLikeVerification(row.type) && hasFeature(row, SEGMENT_FEATURES.KM) && row.kilometers != null
                    ? <><IconWithText icon={GaugeIcon} value={row.kilometers} suffix={t('car_verification.list.kms')} isNumeric={true} /></>
                    : <>-</>,
            },
            {
                field: 'kmEndDisplay',
                name: t('car_verification.list.km_end') as string,
                orderColumn: 'v.kilometers_finish',
                width: 120,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: `${t('common.unit')}: ${t('car_verification.list.kms')}`,
                exportValue: row => isTripLikeVerification(row.type) && hasFeature(row, SEGMENT_FEATURES.KM) && row.kilometersFinish != null
                    ? String(row.kilometersFinish)
                    : '',
                renderCell: row => isTripLikeVerification(row.type) && hasFeature(row, SEGMENT_FEATURES.KM) && row.kilometersFinish != null
                    ? <><IconWithText icon={GaugeIcon} value={row.kilometersFinish} suffix={t('car_verification.list.kms')} isNumeric={true} /></>
                    : <>-</>,
            },
            {
                field: 'kmTripDisplay',
                name: t('car_verification.list.km_trip') as string,
                hideOrderButton: true,
                width: 120,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: `${t('common.unit')}: ${t('car_verification.list.kms')}`,
                exportValue: row => hasFeature(row, SEGMENT_FEATURES.KM) && row.totalKms != null ? String(row.totalKms) : '',
                renderCell: row => hasFeature(row, SEGMENT_FEATURES.KM) && row.totalKms != null
                    ? <><IconWithText icon={DriveIcon} value={row.totalKms} suffix={t('car_verification.list.kms')} isNumeric={true} /></>
                    : <>-</>,
            },
            {
                field: 'hoursStartDisplay',
                name: t('car_verification.list.hours_start') as string,
                orderColumn: 'v.hours_meter',
                width: 120,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: `${t('common.unit')}: ${t('common.hours_symbol')}`,
                exportValue: row => row.type === VerificationType.HOURS_UTILIZATION && hasFeature(row, SEGMENT_FEATURES.HOURS) && row.hoursMeter != null
                    ? String(row.hoursMeter)
                    : '',
                renderCell: row => row.type === VerificationType.HOURS_UTILIZATION && hasFeature(row, SEGMENT_FEATURES.HOURS) && row.hoursMeter != null
                    ? <><IconWithText icon={ClockIcon} value={row.hoursMeter} suffix={t('common.hours_symbol')} isNumeric={true} /></>
                    : <>-</>,
            },
            {
                field: 'hoursEndDisplay',
                name: t('car_verification.list.hours_end') as string,
                orderColumn: 'v.hours_meter_finish',
                width: 120,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: `${t('common.unit')}: ${t('common.hours_symbol')}`,
                exportValue: row => row.type === VerificationType.HOURS_UTILIZATION && hasFeature(row, SEGMENT_FEATURES.HOURS) && row.hoursMeterFinish != null
                    ? String(row.hoursMeterFinish)
                    : '',
                renderCell: row => row.type === VerificationType.HOURS_UTILIZATION && hasFeature(row, SEGMENT_FEATURES.HOURS) && row.hoursMeterFinish != null
                    ? <><IconWithText icon={ClockIcon} value={row.hoursMeterFinish} suffix={t('common.hours_symbol')} isNumeric={true} /></>
                    : <>-</>,
            },
            {
                field: 'hoursTotalDisplay',
                name: t('car_verification.list.hours_total') as string,
                hideOrderButton: true,
                width: 120,
                hideOn: ['sm', 'md', 'lg'],
                headerNote: `${t('common.unit')}: ${t('common.hours_symbol')}`,
                exportValue: row => hasFeature(row, SEGMENT_FEATURES.HOURS) && row.totalHours != null ? String(row.totalHours) : '',
                renderCell: row => hasFeature(row, SEGMENT_FEATURES.HOURS) && row.totalHours != null
                    ? <><IconWithText icon={HourGlassIcon} value={row.totalHours} suffix={t('common.hours_symbol')} isNumeric={true} /></>
                    : <>-</>,
            },
            {
                field: 'autonomyDisplay',
                name: t('car_verification.list.autonomy_col') as string,
                orderColumn: 'v.autonomy',
                width: 100,
                hideOn: ['sm', 'md', 'lg', 'xl'],
                headerNote: `${t('common.unit')}: ${t('car_verification.list.autonomy_symbol')}`,
                exportValue: row => hasFeature(row, SEGMENT_FEATURES.FUEL) && row.autonomy != null ? String(row.autonomy) : '',
                renderCell: row => hasFeature(row, SEGMENT_FEATURES.FUEL) && row.autonomy != null
                    ? <IconWithText
                        icon={CarburantIcon}
                        value={row.autonomy}
                        suffix={t('car_verification.list.autonomy_symbol')}
                        isNumeric={true}
                    />
                    : <>-</>,
            },
            {
                field: 'annotationsDisplay',
                name: t('car_verification.list.annotations') as string,
                hideOrderButton: true,
                width: 130,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => `${row.numberAnnotationsUrgent ?? 0} / ${row.numberAnnotationsInformative ?? 0} / ${row.numberAnnotationsIntervention ?? 0}`,
                renderCell: renderAnnotationsCell,
            },
        ];

        if (!vehicleId) {
            cols.unshift(
                {
                    field: 'vehicleImage',
                    name: t('common.image') as string,
                    hideOrderButton: true,
                    width: 75,
                    hideOn: ['sm', 'md'],
                    excludeFromExport: true,
                    renderCell: row =>
                        <div className={styles.imageDescCell}>
                            <div className={styles.contentImage}>
                                <div className={styles.img} style={{ width: '5rem', height: '3rem', backgroundImage: row?.vehiclePhotoUrl ? 'url(' + row?.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')' : row?.vehicleSegmentDefaultPhotoUrl ? 'url(' + row?.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')' : 'url(' + DefaultAssetImage + ')' }} />
                            </div>
                        </div>,
                },
                {
                    field: 'vehicleRegistration',
                    name: t('car_verification.list.registration') as string,
                    orderColumn: 'vehicles.registration_number',
                    width: 130,
                    renderCell: row =>
                        <div className={styles.descCell}>
                            <div className={styles.columnPlate}>{row.registrationNumber}</div>
                        </div>,
                },
                {
                    field: 'vehicleBrand',
                    name: t('car_verification.list.brand') as string,
                    orderColumn: 'vb.name',
                    width: 100,
                    hideOn: ['sm'],
                    renderCell: row =>
                        <div className={styles.columnBrand}>
                            {row.vehicleBrandName || '-'}
                        </div>,
                },
                {
                    field: 'vehicleModel',
                    name: t('car_verification.list.model') as string,
                    orderColumn: 'vm.name',
                    width: 100,
                    hideOn: ['sm'],
                    renderCell: row =>
                        <div className={styles.columnBrand}>
                            {row.vehicleModelName || '-'}
                        </div>,
                },
                {
                    field: 'vehicleSegmentDisplay',
                    name: t('vehicle.type') as string,
                    orderColumn: 'vehicle_segments.name',
                    width: 130,
                    hideOn: ['sm', 'md', 'lg'],
                    exportValue: row => row.vehicleSegmentName || '-',
                    renderCell: row => <>{row.vehicleSegmentName || '-'}</>,
                },
                {
                    field: 'vehicleLocalDisplay',
                    name: t('vehicle.local') as string,
                    hideOrderButton: true,
                    width: 130,
                    hideOn: ['sm', 'md', 'lg'],
                    exportValue: row => row.vehicleLocalNames || '-',
                    renderCell: row => <>{row.vehicleLocalNames || '-'}</>,
                }
            );
        }

        cols.push(...customFieldColumns);

        if (vehicleId && vehicle?.segmentFeatures) {
            const features = vehicle.segmentFeatures;
            const kmFields = ['kilometersDisplay', 'kmStartDisplay', 'kmEndDisplay', 'kmTripDisplay'];
            const fuelFields = ['autonomyDisplay'];
            const hoursFields = ['hoursStartDisplay', 'hoursEndDisplay', 'hoursTotalDisplay'];
            return cols.filter(c => {
                const f = c.field as string;
                if (kmFields.includes(f)) return features.includes(SEGMENT_FEATURES.KM);
                if (fuelFields.includes(f)) return features.includes(SEGMENT_FEATURES.FUEL);
                if (hoursFields.includes(f)) return features.includes(SEGMENT_FEATURES.HOURS);
                return true;
            });
        }

        return cols;
    }, [vehicleId, customFieldColumns, vehicle?.segmentFeatures]);

    const toTableRow = (item: CarVerificationListItemDto): VerificationTableRow => ({
        ...item,
        vehicleRegistration: item.registrationNumber || '',
        vehicleBrand: item.vehicleBrandName || '',
        vehicleModel: item.vehicleModelName || '',
        vehicleImage: item.vehiclePhotoUrl || '',
        typeName: item.type || '',
        driverDisplay: item.driverName || item.responsibleName || '',
        startDateDisplay: item.verificationDate ? new Date(item.verificationDate) : undefined,
        endDateDisplay: item.verificationFinishDate ? new Date(item.verificationFinishDate) : undefined,
        kilometersDisplay: item.kilometers ?? '',
        kmStartDisplay: isTripLikeVerification(item.type) ? (item.kilometers ?? '') : '',
        kmEndDisplay: isTripLikeVerification(item.type) ? (item.kilometersFinish ?? '') : '',
        kmTripDisplay: item.totalKms ?? '',
        autonomyDisplay: item.autonomy ?? '',
        hoursStartDisplay: item.type === VerificationType.HOURS_UTILIZATION ? (item.hoursMeter ?? '') : '',
        hoursEndDisplay: item.type === VerificationType.HOURS_UTILIZATION ? (item.hoursMeterFinish ?? '') : '',
        hoursTotalDisplay: item.totalHours ?? '',
        annotationsDisplay: `${item.numberAnnotationsUrgent ?? 0}/${item.numberAnnotationsInformative ?? 0}/${item.numberAnnotationsIntervention ?? 0}`,
        vehicleSegmentDisplay: item.vehicleSegmentName || '',
        vehicleLocalDisplay: item.vehicleLocalNames || '',
        vehicleSegmentFeaturesArr: (item.vehicleSegmentFeatures ?? '').split(',').map(x => x.trim()).filter(x => x.length > 0),
    });

    const tableRows: VerificationTableRow[] = useMemo(() => {
        return items.map(toTableRow);
    }, [items]);

    const fetchAllForExcelExport = useCallback(async (): Promise<VerificationTableRow[]> => {
        if (!onFetchAllForExport) return [];
        const allVerifications = await onFetchAllForExport();
        return allVerifications.map(toTableRow);
    }, [onFetchAllForExport]);

    const handleOrderChange = (orderData: OrderData<VerificationTableRow>) => {
        const orderColumn = orderData.orderColumn || (orderData.field as string);
        onTableFilter(orderColumn, orderData.isOrderAsc);
    };

    const getFieldFromOrderColumn = (orderColumn: string | undefined): keyof VerificationTableRow => {
        const mapping: Record<string, keyof VerificationTableRow> = {
            'v.type': 'typeName',
            'displayUserName': 'driverDisplay',
            'v.verification_date': 'startDateDisplay',
            'v.verification_finish_date': 'endDateDisplay',
            'v.kilometers': 'kilometersDisplay',
            'v.kilometers_finish': 'kmEndDisplay',
            'v.hours_meter': 'hoursStartDisplay',
            'v.hours_meter_finish': 'hoursEndDisplay',
            'v.autonomy': 'autonomyDisplay',
            'vehicles.registration_number': 'vehicleRegistration',
            'vb.name': 'vehicleBrand',
            'vm.name': 'vehicleModel',
            'vehicle_segments.name': 'vehicleSegmentDisplay',
        };
        return mapping[orderColumn ?? ''] ?? 'startDateDisplay';
    };

    const defaultOrder: OrderData<VerificationTableRow> = useMemo(() => {
        const orderColumn = criteria?.orderColumn ?? 'v.verification_date';
        return {
            field: getFieldFromOrderColumn(orderColumn),
            orderColumn: orderColumn,
            isOrderAsc: criteria?.orderBy === 'asc',
        };
    }, [criteria?.orderColumn, criteria?.orderBy]);

    return (
        <div className={styles.tableContainer}>
            <DataTableWithSidebar
                key={`${reRenderSearch}`}
                cacheKey={`verification-list-v2-${vehicleId || 'all'}`}
                columns={tableColumns}
                rows={tableRows}
                defaultHiddenColumns={['vehicleSegmentDisplay', 'vehicleLocalDisplay']}
                defaultOrder={defaultOrder}
                onRowClick={(row, _, event) => onClickItem(row, event)}
                onOrderChange={handleOrderChange}
                onRenderAction={renderTableActionCell}
                onHref={row => vehicleId ? `/vehicles/details/${vehicleId}/${tabId}/details/${row.id}` : `/verifications/details/${row.id}`}
                onRowId={row => row.id ?? ''}
                scrollToRowId={scrollToRowId}
                infiniteScroll={{
                    isLoading: isLoadingMore,
                    hasMore: hasMore,
                    onLoadMore: onLoadMore,
                    hasMorePrevious: hasMorePrevious,
                    isLoadingPrevious: isLoadingPrevious,
                    onLoadPrevious: onLoadPrevious,
                }}
                exportExcel={onFetchAllForExport ? {
                    fetchAll: fetchAllForExcelExport,
                    fileName: t('car_verification.title') as string,
                } : undefined}
                exportRef={exportRef}
                hideExportInSidebar={!!exportRef}
            />
            <QuestionYesNo
                onNo={onCancelRemove}
                onYes={onRemove}
                isVisible={showRemoveModal}
                message={t('common.messages.remove_record')} />

            <RemoveVerificationModal
                isVisible={showRemoveWithReplacementModal}
                onCancelRemove={onCancelRemove}
                onRemove={onRemove}
                onRemoveAndFinishReplacement={onRemoveAndFinishReplacement}
                onRemoveAndStartReplacement={onRemoveAndFinishReplacement}
                verificationImmobilized={itemToRemove?.immobilized}
                lastVerificationImmobilized={lastVerificationImmobilized}
                hasReplacement={Boolean(vehicle?.replacedVehicle)}
                isReplacementFromThisVerification={vehicle?.replacementVerificationId === itemToRemove?.id}
            />

            <VehicleReplacementModal
                isVisible={dialogReplacementVehicleIsOpen}
                currentVehicle={vehicle as any}
                replacedVehicle={vehicle?.replacedVehicle ? vehicle?.id : undefined}
                verificationId={verificationSelected?.id}
                onSave={async () => {
                    setVerificationSelected(null);
                    setDialogReplacementVehicleIsOpen(false);
                    onVerificationsUpdated && await onVerificationsUpdated();
                }}
                onCancel={() => setDialogReplacementVehicleIsOpen(false)}
            />

            <ReactTooltip />
        </div>
    );
};

export default VerificationListTable;
